import React from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from 'recharts';
import { scaleLinear } from 'd3-scale';

function WeatherChart({
  data,
  startDate,
  endDate,
  interval,
  unitSettings,
  calculateDomain,
  chartConfig = {},
}) {
  /* ---------- early exit ---------- */
  if (!data || data.length === 0) return <p>No data available for this chart.</p>;

  /* ---------- config ---------- */
  const {
    dataKey = '',
    dataKeys = [],
    minKey  = '',
    maxKey  = '',
    color   = '#8884d8',
    colors,
    fillId  = 'defaultFill',
    domainMargin = 5,
    yAxisLabel   = '',
    showBand = false, 
    bandColors = [color, color],
  } = chartConfig;

  /* ---------- guard: need keys ---------- */
  if (dataKeys.length === 0 && (!dataKey || !minKey || !maxKey)) {
    return <p>Invalid chart configuration.</p>;
  }

  /* ---------- shaded-band data ---------- */
  const baseKey = `${minKey || 'band'}__base`;
  const bandKey = `${minKey || 'band'}__range`;

  const chartData =
    minKey && maxKey
      ? data.map(d => {
          const low  = Math.min(d[minKey], d[maxKey]);
          const high = Math.max(d[minKey], d[maxKey]);
          return { ...d, [baseKey]: low, [bandKey]: high - low };
        })
      : data;

  /* ---------- colours for lines ---------- */
  const lineColors =
    Array.isArray(colors) && colors.length ? colors : [color];

  /* ---------- y-axis domain ---------- */
  let rawDomain;
  if (dataKeys.length > 0) {
    const vals = data.flatMap(d =>
      dataKeys.map(k => d[k]).filter(v => v != null && !isNaN(v))
    );
    rawDomain = [
      Math.min(...vals) - domainMargin,
      Math.max(...vals) + domainMargin,
    ];
  } else {
    rawDomain = calculateDomain(data, minKey, maxKey, domainMargin);
  }

  /* fallback */
  if (isNaN(rawDomain[0]) || isNaN(rawDomain[1])) {
    const vals = data
      .map(d => d[dataKey])
      .filter(v => v != null && !isNaN(v));
    if (vals.length) {
      const lo = Math.min(...vals);
      const hi = Math.max(...vals);
      const m  = (hi - lo) * (domainMargin / 100);
      rawDomain = [lo - m, hi + m];
    }
  }

  let minY = Math.floor(rawDomain[0]);
  let maxY = Math.ceil(rawDomain[1]);
  if (dataKey === 'watts') minY = 0;

  // Create nice scale and get ticks
  const niceScale = scaleLinear().domain([minY, maxY]).nice(6);
  let allTicks = niceScale.ticks(6);
  
  // Find the actual data range to ensure we include all data points
  let dataMin, dataMax;
  if (dataKeys.length > 0) {
    const vals = data.flatMap(d =>
      dataKeys.map(k => d[k]).filter(v => v != null && !isNaN(v))
    );
    dataMin = Math.min(...vals);
    dataMax = Math.max(...vals);
  } else if (minKey && maxKey) {
    const vals = data.flatMap(d => [d[minKey], d[maxKey]]).filter(v => v != null && !isNaN(v));
    dataMin = Math.min(...vals);
    dataMax = Math.max(...vals);
  } else {
    const vals = data.map(d => d[dataKey]).filter(v => v != null && !isNaN(v));
    dataMin = Math.min(...vals);
    dataMax = Math.max(...vals);
  }
  
  // Ensure we have ticks that cover all data points
  const ticksNeeded = allTicks.filter(t => t <= dataMin || t >= dataMax || (t >= dataMin && t <= dataMax));
  
  // If we don't have enough coverage, expand the tick range
  let ticks = allTicks;
  if (ticksNeeded.length < 2 || allTicks[0] > dataMin || allTicks[allTicks.length - 1] < dataMax) {
    // Expand the domain to ensure data coverage
    const expandedScale = scaleLinear()
      .domain([Math.min(allTicks[0], dataMin - 2), Math.max(allTicks[allTicks.length - 1], dataMax + 2)])
      .nice(6);
    ticks = expandedScale.ticks(6);
  }
  
  // Ensure we have 5-6 ticks minimum
  if (ticks.length < 5) {
    const expandedScale = scaleLinear().domain([ticks[0], ticks[ticks.length - 1]]).nice(7);
    ticks = expandedScale.ticks(7);
  }
  
  // Use the actual first and last tick as our domain
  const niceDomain = [ticks[0], ticks[ticks.length - 1]];

  const rng = maxY - minY;
  let decimals = 0;

  if (ticks.length > 1) {
    const step = ticks[1] - ticks[0];
    if (step < 1 || rng < 5) decimals = step < 0.1 ? 2 : 1;
    const fract = data.some(d => {
      const v = d[dataKey];
      return v && v % 1 !== 0 && v % 1 > 0.01;
    });
    if (fract && decimals === 0 && rng < 10) decimals = 1;
  }

  const yAxisProps = {
    yAxisId: 'left',
    type: 'number',
    allowDecimals: true,
    domain: [() => niceDomain[0], () => niceDomain[1]],
    ticks,
    axisLine: { stroke: '#ccc', strokeWidth: 1 },
    tickLine: { stroke: '#ccc', strokeWidth: 1 },
    width: 100,
    tickMargin: 10,
    tickFormatter: v => (typeof v === 'number' ? v.toFixed(decimals) : v),
  };

  /* ---------- x-axis tick formatter ---------- */
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const hourly  = new Date(endDate) - new Date(startDate) <= ONE_DAY;
  const fmtTick = v => {
    const d = new Date(Number(v));
    if (hourly) {
      const h = d.getHours();
      if (h % 2) return '';
      const suf = h < 12 ? 'AM' : 'PM';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${h12}${suf}`;
    }
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  /* ---------- tooltip helpers ---------- */
  const unit = {
    temperature    : `°${unitSettings.t2m_units}`,
    temperatureMin : `°${unitSettings.t2m_units}`,
    temperatureMax : `°${unitSettings.t2m_units}`,
    precipitation  : 'mm',
    cloud          : '%',
    windSpeed      : unitSettings.ws2m_units,
    watts          : 'W',
    rh2m           : '%',
    [bandKey]      : `°${unitSettings.t2m_units}`, // Add unit for band range
  };
  const label = {
    temperature    : 'Average',
    temperatureMin : 'Min',
    temperatureMax : 'Max',
    precipitation  : 'Rain',
    cloud          : 'Cloud Cover',
    windSpeed      : 'Wind Speed',
    watts          : 'Solar Watts',
    rh2m           : 'Humidity',
    [bandKey]      : 'Temp Range', // Better label for band range
  };

  /* ---------- render ---------- */
  return (
    <ResponsiveContainer width="100%" height={800}>
      <ComposedChart
        data={chartData}
        margin={{ top: 16, right: 32, left: 8, bottom: 16 }}
      >

      <Legend
        verticalAlign="top"
        align="right"
        formatter={(value) => (
          <span style={{ fontWeight: 'bold', color: '#000' }}>{value}</span>
        )}
      />

      {showBand && (
        <defs>
          <linearGradient id={`${fillId}-band`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={bandColors[0]} stopOpacity={0.6} />
            <stop offset="100%" stopColor={bandColors[1]} stopOpacity={0.6} />
          </linearGradient>
        </defs>
      )}
        {/* gradient reused for band fill */}
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={color} stopOpacity={1} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>

        {/* axes */}
        <XAxis
          dataKey="date"
          axisLine={{ stroke: '#ccc', strokeWidth: 1 }}
          tickLine={{ stroke: '#ccc', strokeWidth: 1 }}
          interval={interval}
          tickCount={8}
          tickFormatter={fmtTick}
        />
        <YAxis {...yAxisProps}>
          <Label
            value={
              typeof yAxisLabel === 'function'
                ? yAxisLabel(unitSettings)
                : yAxisLabel
            }
            angle={-90}
            position="insideLeft"
            style={{ textAnchor: 'middle' }}
          />
        </YAxis>

        {/* tooltip */}
        <Tooltip
          formatter={(v, n) => {
            // Hide the base key from tooltip
            if (n === baseKey) return [null, null];
            
            return [
              `${Number(v).toFixed(decimals)} ${unit[n] || ''}`,
              label[n] || n,
            ];
          }}
          labelFormatter={ts =>
            new Date(Number(ts)).toLocaleString(undefined, {
              month: 'short',
              day  : 'numeric',
              hour : 'numeric',
              minute: 'numeric',
            })
          }
        />

        {/* shaded band (drawn first so lines sit on top) */}
        {showBand && minKey && maxKey && (
          <>
            <Area
              yAxisId="left"
              dataKey={baseKey}
              type="monotone"
              stackId="band"
              stroke="none"
              fillOpacity={0}
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              yAxisId="left"
              dataKey={bandKey}
              type="monotone"
              stackId="band"
              stroke="none"
              fill={`url(#${fillId}-band)`} 
              fillOpacity={1}
              isAnimationActive={false}
              legendType="none"
            />
          </>
        )}

        {/* lines or single area */}
        {dataKeys.length > 0 ? (
          dataKeys.map((k, i) => (
            <Line
              key={k}
              yAxisId="left"
              type="monotone"
              dataKey={k}
              stroke={lineColors[i]}
              strokeWidth={3}
              dot={false}
              name={label[k] || k}
            />
          ))
        ) : (
          <Area
            yAxisId="left"
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={3}
            fill={`url(#${fillId})`}
            fillOpacity={1}
            name={label[dataKey] || dataKey}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default WeatherChart;